import { useContext, type ChangeEvent } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject, formatDate } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useInvoice, { useInvoiceForm } from '@hooks/useInvoice'

import useGeoCountries from '@hooks/useGeoCountries'

const MODAL_KEY = 'ManageInvoiceBillingDetailsModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateInvoice: (payload: HandleCallbackActionParams) => void,
}

const customRequiredFields = [
  { key: 'currency_code' },
  { key: 'due_date' },
  { key: 'order_title' },
  { key: 'reference' },
]

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageInvoiceBillingDetailsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateInvoice } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const invoice = digObject(modalPayload, 'invoice')

  const invoicePayload = useInvoice(invoice)
  const {
    callbacks: { updateInvoice: updateFn },
    updating,
  } = invoicePayload

  const invoiceFormPayload = useInvoiceForm(invoice, {
    customRequiredFields,
    validateOn: 'change',
  })

  const {
    entityState,
    entityState: {
      currency_code, due_date, notes, order_title, reference,
    },
    setEntityState,
    handlers,
    saveEnabled,
  } = invoiceFormPayload

  const geoCountriesPayload = useGeoCountries({
    performHttpRequests: true,
  })
  const { filteredGeoCountries } = geoCountriesPayload

  const createdDate = digObject(entityState, 'dates.created.date', '')
  const formattedDate = formatDate(createdDate, 'dd/MM/yyyy')

  const updateInvoicePayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: {
      ...entityState,
    },
    shouldRedirect: true,
    toastText: 'Billing Details Updated',
  }

  return (
    <SidebarModal
      callbacks={callbacks}
      clickSafeZone
      modalKey={MODAL_KEY}
      showModal={showModal}
    >
      <SidebarModal.Header
        callbacks={callbacks}
        title="Manage"
        titleSecondLine="Billing Details"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Date">
            <input name="created_date" type="date" value={formattedDate} readOnly disabled />
          </Form.Field>

          <Form.Field label="* Due Date" marginTop="large">
            <input name="due_date" type="date" value={due_date} {...handlers} />
          </Form.Field>

          <Form.Field label="* Reference" marginTop="large">
            <input name="reference" type="text" value={reference} {...handlers} />
          </Form.Field>

          <Form.Field label="* Order Title" marginTop="large">
            <input
              name="order_title"
              type="text"
              value={order_title}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="* Currency" marginTop="large">
            <select
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setEntityState({ currency_code: e.target.value })}
              value={currency_code}
            >
              <option value="">Please Select...</option>
              {filteredGeoCountries.map(geoCountry => (
                <option key={geoCountry.id} value={geoCountry.currency_code}>{geoCountry.currency_code}</option>
              ))}
            </select>
          </Form.Field>

          <Form.Field label="Customere Notes" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea name="notes" style={{ height: 80, resize: 'vertical' }} value={notes} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          loadingBubbleColor="white"
          onClick={() => updateInvoice(updateInvoicePayload)}
          size="large"
        >
          {saveEnabled ? 'Save Billing Details' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageInvoiceBillingDetailsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
